"use client";

import { c as _c } from "react/compiler-runtime";
import * as React from "react";
import * as TabsPrimitive from "@radix-ui/react-tabs";
import { cva, type VariantProps } from "class-variance-authority";
import { cn, getPlatformClass } from "@/lib/utils";
import Anchor from "@shared-ui/components/Anchor";
import { usePathname } from "next/navigation";

// ============================================================================
// TabsList Variants
// ============================================================================
const tabsListVariants = cva("inline-flex items-center justify-center", {
  variants: {
    variant: {
      default: "rounded-xl bg-gray-100 p-1.5",
      line: "border-b border-gray-200 gap-x-4 bg-transparent p-0",
      navigation: "border-b border-gray-200 gap-x-8 bg-transparent p-0",
      sliding: "bg-white rounded-2xl p-1 shadow-sm overflow-hidden",
      // Pill uses CSS Grid for perfect tab/indicator alignment (grid-template-columns set via inline style)
      // Note: p-1 (8px total) matches the indicator calc((100% - 8px) / tabCount)
      // items-center ensures tabs are vertically centered within the grid row
      pill: "grid items-center bg-cream p-1 rounded-button"
    },
    size: {
      sm: "h-9",
      default: "h-10",
      lg: "h-11"
    }
  },
  // Pill variant should auto-size to content, not use fixed heights from size variants.
  // Compound variants are applied AFTER regular variants, so h-auto overrides h-10/h-9/h-11.
  // This ensures indicator (top-1 bottom-1) and tabs have the same reference height.
  compoundVariants: [{
    variant: "pill",
    className: "h-auto"
  }],
  defaultVariants: {
    variant: "default",
    size: "default"
  }
});

// ============================================================================
// TabsTrigger Variants
// ============================================================================
const tabsTriggerVariants = cva("inline-flex items-center justify-center whitespace-nowrap font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50", {
  variants: {
    variant: {
      default: ["rounded-lg px-3.5 py-2 text-sm", "text-gray-600 hover:text-gray-800", "data-[state=active]:bg-white data-[state=active]:text-gray-900 data-[state=active]:shadow-sm"],
      line: ["border-b-2 border-transparent px-4 pb-2 text-sm", "text-gray-400 hover:text-gray-500 hover:border-gray-300", "data-[state=active]:border-gray-900 data-[state=active]:text-gray-900"],
      navigation: ["border-b-2 border-transparent px-1 py-4 text-sm", "text-gray-500 hover:text-gray-700 hover:border-gray-300", "data-[state=active]:border-indigo-500 data-[state=active]:text-indigo-600"],
      sliding: ["relative z-10 px-6 py-3 text-center rounded-xl text-sm font-medium", "text-slate-800 transition-colors duration-300", "data-[state=active]:text-white"],
      // Pill variant - active styling from animated indicator, tabs sized by CSS Grid (not flex)
      pill: ["relative px-6 py-2.5 text-base font-semibold rounded-badge text-center", "text-black/60 hover:text-black", "data-[state=active]:text-black"]
    },
    size: {
      sm: "text-xs px-2.5 py-1.5",
      default: "text-sm px-3.5 py-2",
      lg: "text-base px-4 py-2.5"
    }
  },
  defaultVariants: {
    variant: "default",
    size: "default"
  }
});

// ============================================================================
// Context for sharing variant and animated indicator state
// ============================================================================
interface TabsContextValue {
  variant: VariantProps<typeof tabsTriggerVariants>["variant"];
  size: VariantProps<typeof tabsTriggerVariants>["size"];
  platform?: "instagram" | "tiktok";
  animatedIndicator?: boolean;
  registerTrigger?: (value: string, element: HTMLButtonElement | null) => void;
}
const TabsContext = React.createContext<TabsContextValue>({
  variant: "default",
  size: "default",
  animatedIndicator: false
});

// ============================================================================
// Tabs Root
// ============================================================================
const Tabs = TabsPrimitive.Root;

// ============================================================================
// TabsList
// ============================================================================
interface TabsListProps extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>, VariantProps<typeof tabsListVariants> {
  animatedIndicator?: boolean;
  indicatorClassName?: string;
  platform?: "instagram" | "tiktok";
}
const TabsList = React.forwardRef<React.ElementRef<typeof TabsPrimitive.List>, TabsListProps>((t0, ref) => {
  const $ = _c(64);
  let children;
  let className;
  let indicatorClassName;
  let platform;
  let props;
  let size;
  let t1;
  let variant;
  if ($[0] !== t0) {
    ({
      className,
      variant,
      size,
      animatedIndicator: t1,
      indicatorClassName,
      platform,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = indicatorClassName;
    $[4] = platform;
    $[5] = props;
    $[6] = size;
    $[7] = t1;
    $[8] = variant;
  } else {
    children = $[1];
    className = $[2];
    indicatorClassName = $[3];
    platform = $[4];
    props = $[5];
    size = $[6];
    t1 = $[7];
    variant = $[8];
  }
  const animatedIndicator = t1 === undefined ? false : t1;
  let t2;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = new Map();
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const triggersRef = React.useRef(t2);
  let t3;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      left: 0,
      width: 0
    };
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const [indicatorStyle, setIndicatorStyle] = React.useState(t3);
  React.useState(null);
  const [indicatorReady, setIndicatorReady] = React.useState(false);
  const listRef = React.useRef(null);
  let t4;
  if ($[11] !== children) {
    t4 = React.Children.toArray(children).filter(_temp).length || 2;
    $[11] = children;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const tabCount = t4;
  const [activeIndex, setActiveIndex] = React.useState(0);
  const [, setIsHydrated] = React.useState(false);
  let t5;
  let t6;
  if ($[13] !== animatedIndicator || $[14] !== variant) {
    t5 = () => {
      setIsHydrated(true);
      if (!animatedIndicator || !listRef.current) {
        return;
      }
      const updateIndicator = () => {
        if (!listRef.current) {
          return;
        }
        const tabs = Array.from(listRef.current.querySelectorAll("[role=\"tab\"]"));
        const activeTabIndex = tabs.findIndex(_temp2);
        if (activeTabIndex >= 0) {
          setActiveIndex(activeTabIndex);
        }
        if (variant !== "pill") {
          const activeButton = listRef.current.querySelector("[data-state=\"active\"]") as HTMLButtonElement;
          if (activeButton) {
            setIndicatorStyle({
              left: activeButton.offsetLeft,
              width: activeButton.clientWidth
            });
          }
        }
        setIndicatorReady(true);
      };
      const observer = new MutationObserver(updateIndicator);
      observer.observe(listRef.current, {
        attributes: true,
        subtree: true,
        attributeFilter: ["data-state"]
      });
      updateIndicator();
      return () => observer.disconnect();
    };
    t6 = [animatedIndicator, variant];
    $[13] = animatedIndicator;
    $[14] = variant;
    $[15] = t5;
    $[16] = t6;
  } else {
    t5 = $[15];
    t6 = $[16];
  }
  React.useEffect(t5, t6);
  let t7;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = (value, element) => {
      if (element) {
        triggersRef.current.set(value, element);
      } else {
        triggersRef.current.delete(value);
      }
    };
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const registerTrigger = t7;
  const t8 = animatedIndicator ? registerTrigger : undefined;
  let t9;
  if ($[18] !== animatedIndicator || $[19] !== platform || $[20] !== size || $[21] !== t8 || $[22] !== variant) {
    t9 = {
      variant,
      size,
      platform,
      animatedIndicator,
      registerTrigger: t8
    };
    $[18] = animatedIndicator;
    $[19] = platform;
    $[20] = size;
    $[21] = t8;
    $[22] = variant;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  const contextValue = t9;
  let t10;
  if ($[24] !== indicatorClassName || $[25] !== variant) {
    t10 = () => {
      if (indicatorClassName) {
        return indicatorClassName;
      }
      switch (variant) {
        case "sliding":
          {
            return "bg-gradient-to-t from-indigo-600 to-blue-500 shadow-xl shadow-indigo-500/25";
          }
        case "default":
        case "pill":
          {
            return "bg-white shadow-sm";
          }
        default:
          {
            return "bg-white shadow-sm";
          }
      }
    };
    $[24] = indicatorClassName;
    $[25] = variant;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  const getIndicatorBg = t10;
  let t11;
  if ($[27] !== className || $[28] !== size || $[29] !== variant) {
    t11 = cn("relative", tabsListVariants({
      variant,
      size,
      className
    }));
    $[27] = className;
    $[28] = size;
    $[29] = variant;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  const listClassName = t11;
  let t12;
  if ($[31] !== tabCount || $[32] !== variant) {
    t12 = variant === "pill" ? {
      gridTemplateColumns: `repeat(${tabCount}, 1fr)`
    } as React.CSSProperties : undefined;
    $[31] = tabCount;
    $[32] = variant;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  const listStyle = t12;
  let t13;
  if ($[34] !== ref) {
    t13 = node => {
      if (typeof ref === "function") {
        ref(node);
      } else {
        if (ref) {
          ref.current = node;
        }
      }
      listRef.current = node;
    };
    $[34] = ref;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  const handleRef = t13;
  let t14;
  if ($[36] !== activeIndex || $[37] !== animatedIndicator || $[38] !== getIndicatorBg || $[39] !== indicatorReady || $[40] !== indicatorStyle || $[41] !== tabCount || $[42] !== variant) {
    t14 = animatedIndicator && <span className={cn("absolute rounded-md", "transition-transform duration-300 ease-out", variant === "default" && "bottom-1 top-1", variant === "sliding" && "bottom-1 top-1 rounded-xl", variant === "pill" && "left-1 top-1 bottom-1 rounded-lg -z-0", getIndicatorBg())} style={variant === "pill" ? {
      width: `calc((100% - 8px) / ${tabCount})`,
      transform: `translateX(${activeIndex * 100}%)`
    } : indicatorReady && indicatorStyle.width > 0 ? {
      left: indicatorStyle.left,
      width: indicatorStyle.width
    } : {
      left: 6,
      width: "calc(50% - 4px)"
    }} />;
    $[36] = activeIndex;
    $[37] = animatedIndicator;
    $[38] = getIndicatorBg;
    $[39] = indicatorReady;
    $[40] = indicatorStyle;
    $[41] = tabCount;
    $[42] = variant;
    $[43] = t14;
  } else {
    t14 = $[43];
  }
  const indicatorElement = t14;
  if (variant === "navigation") {
    let t15;
    if ($[44] !== children || $[45] !== handleRef || $[46] !== indicatorElement || $[47] !== listClassName || $[48] !== listStyle || $[49] !== props) {
      t15 = <div ref={handleRef} className={listClassName} style={listStyle} {...props}>{indicatorElement}{children}</div>;
      $[44] = children;
      $[45] = handleRef;
      $[46] = indicatorElement;
      $[47] = listClassName;
      $[48] = listStyle;
      $[49] = props;
      $[50] = t15;
    } else {
      t15 = $[50];
    }
    let t16;
    if ($[51] !== contextValue || $[52] !== t15) {
      t16 = <TabsContext.Provider value={contextValue}>{t15}</TabsContext.Provider>;
      $[51] = contextValue;
      $[52] = t15;
      $[53] = t16;
    } else {
      t16 = $[53];
    }
    return t16;
  }
  let t15;
  if ($[54] !== children || $[55] !== handleRef || $[56] !== indicatorElement || $[57] !== listClassName || $[58] !== listStyle || $[59] !== props) {
    t15 = <TabsPrimitive.List ref={handleRef} className={listClassName} style={listStyle} {...props}>{indicatorElement}{children}</TabsPrimitive.List>;
    $[54] = children;
    $[55] = handleRef;
    $[56] = indicatorElement;
    $[57] = listClassName;
    $[58] = listStyle;
    $[59] = props;
    $[60] = t15;
  } else {
    t15 = $[60];
  }
  let t16;
  if ($[61] !== contextValue || $[62] !== t15) {
    t16 = <TabsContext.Provider value={contextValue}>{t15}</TabsContext.Provider>;
    $[61] = contextValue;
    $[62] = t15;
    $[63] = t16;
  } else {
    t16 = $[63];
  }
  return t16;
});
TabsList.displayName = "TabsList";

// ============================================================================
// TabsTrigger
// ============================================================================
interface TabsTriggerProps extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>, Omit<VariantProps<typeof tabsTriggerVariants>, "variant" | "size"> {
  icon?: React.ReactNode;
  iconPosition?: "left" | "right";
  badge?: React.ReactNode;
  variant?: VariantProps<typeof tabsTriggerVariants>["variant"];
  size?: VariantProps<typeof tabsTriggerVariants>["size"];
}
const TabsTrigger = React.forwardRef<React.ElementRef<typeof TabsPrimitive.Trigger>, TabsTriggerProps>((t0, ref) => {
  const $ = _c(35);
  let badge;
  let children;
  let className;
  let icon;
  let props;
  let sizeProp;
  let t1;
  let value;
  let variantProp;
  if ($[0] !== t0) {
    ({
      className,
      variant: variantProp,
      size: sizeProp,
      icon,
      iconPosition: t1,
      badge,
      children,
      value,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = badge;
    $[2] = children;
    $[3] = className;
    $[4] = icon;
    $[5] = props;
    $[6] = sizeProp;
    $[7] = t1;
    $[8] = value;
    $[9] = variantProp;
  } else {
    badge = $[1];
    children = $[2];
    className = $[3];
    icon = $[4];
    props = $[5];
    sizeProp = $[6];
    t1 = $[7];
    value = $[8];
    variantProp = $[9];
  }
  const iconPosition = t1 === undefined ? "left" : t1;
  const context = React.useContext(TabsContext);
  const variant = variantProp ?? context.variant;
  const size = sizeProp ?? context.size;
  let t2;
  if ($[10] !== context.platform) {
    t2 = () => {
      if (!context.platform) {
        return "";
      }
      return getPlatformClass(context.platform, "data-[state=active]:text-indigo-500", "data-[state=active]:text-black");
    };
    $[10] = context.platform;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  const getPlatformActiveClass = t2;
  let t3;
  if ($[12] !== icon || $[13] !== iconPosition) {
    t3 = icon && <span className={cn("inline-flex items-center justify-center", iconPosition === "left" ? "mr-2" : "ml-2")}>{icon}</span>;
    $[12] = icon;
    $[13] = iconPosition;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  const iconElement = t3;
  let t4;
  if ($[15] !== className || $[16] !== context.platform || $[17] !== getPlatformActiveClass || $[18] !== size || $[19] !== variant) {
    let t5;
    if ($[21] !== context.platform || $[22] !== getPlatformActiveClass) {
      t5 = context.platform && getPlatformActiveClass();
      $[21] = context.platform;
      $[22] = getPlatformActiveClass;
      $[23] = t5;
    } else {
      t5 = $[23];
    }
    t4 = cn("relative z-10", tabsTriggerVariants({
      variant,
      size
    }), t5, className);
    $[15] = className;
    $[16] = context.platform;
    $[17] = getPlatformActiveClass;
    $[18] = size;
    $[19] = variant;
    $[20] = t4;
  } else {
    t4 = $[20];
  }
  const t5 = iconPosition === "left" && iconElement;
  const t6 = iconPosition === "right" && iconElement;
  let t7;
  if ($[24] !== badge) {
    t7 = badge && <span className="relative">{badge}</span>;
    $[24] = badge;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  let t8;
  if ($[26] !== children || $[27] !== props || $[28] !== ref || $[29] !== t4 || $[30] !== t5 || $[31] !== t6 || $[32] !== t7 || $[33] !== value) {
    t8 = <TabsPrimitive.Trigger ref={ref} value={value} className={t4} {...props}>{t5}{children}{t6}{t7}</TabsPrimitive.Trigger>;
    $[26] = children;
    $[27] = props;
    $[28] = ref;
    $[29] = t4;
    $[30] = t5;
    $[31] = t6;
    $[32] = t7;
    $[33] = value;
    $[34] = t8;
  } else {
    t8 = $[34];
  }
  return t8;
});
TabsTrigger.displayName = "TabsTrigger";

// ============================================================================
// TabsContent
// ============================================================================
const TabsContent = React.forwardRef<React.ElementRef<typeof TabsPrimitive.Content>, React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>>((t0, ref) => {
  const $ = _c(9);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("ring-offset-background focus-visible:ring-ring mt-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== ref || $[7] !== t1) {
    t2 = <TabsPrimitive.Content ref={ref} className={t1} {...props} />;
    $[5] = props;
    $[6] = ref;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
});
TabsContent.displayName = "TabsContent";

// ============================================================================
// TabsLink (for router-based navigation)
// ============================================================================
interface TabsLinkProps {
  href: string;
  variant?: VariantProps<typeof tabsTriggerVariants>["variant"];
  size?: VariantProps<typeof tabsTriggerVariants>["size"];
  icon?: React.ReactNode;
  iconPosition?: "left" | "right";
  activeClassName?: string;
  className?: string;
  children?: React.ReactNode;
}
const TabsLink = React.forwardRef<HTMLAnchorElement, TabsLinkProps>((t0, ref) => {
  const $ = _c(20);
  const {
    className,
    href,
    variant: variantProp,
    size: sizeProp,
    icon,
    iconPosition: t1,
    activeClassName,
    children
  } = t0;
  const iconPosition = t1 === undefined ? "left" : t1;
  const pathname = usePathname();
  const context = React.useContext(TabsContext);
  const variant = variantProp ?? context.variant ?? "navigation";
  const size = sizeProp ?? context.size;
  let t2;
  if ($[0] !== href || $[1] !== pathname) {
    t2 = pathname === href || pathname?.endsWith(href);
    $[0] = href;
    $[1] = pathname;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const isActive = t2;
  let t3;
  if ($[3] !== icon || $[4] !== iconPosition) {
    t3 = icon && <span className={cn("inline-flex items-center justify-center", iconPosition === "left" ? "mr-2" : "ml-2")}>{icon}</span>;
    $[3] = icon;
    $[4] = iconPosition;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const iconElement = t3;
  const t4 = isActive ? "active" : "inactive";
  let t5;
  if ($[6] !== activeClassName || $[7] !== className || $[8] !== isActive || $[9] !== size || $[10] !== variant) {
    t5 = cn(tabsTriggerVariants({
      variant,
      size
    }), isActive && activeClassName, className);
    $[6] = activeClassName;
    $[7] = className;
    $[8] = isActive;
    $[9] = size;
    $[10] = variant;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const t6 = iconPosition === "left" && iconElement;
  const t7 = iconPosition === "right" && iconElement;
  let t8;
  if ($[12] !== children || $[13] !== href || $[14] !== ref || $[15] !== t4 || $[16] !== t5 || $[17] !== t6 || $[18] !== t7) {
    t8 = <Anchor ref={ref} href={href} data-state={t4} className={t5}>{t6}{children}{t7}</Anchor>;
    $[12] = children;
    $[13] = href;
    $[14] = ref;
    $[15] = t4;
    $[16] = t5;
    $[17] = t6;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  return t8;
});
TabsLink.displayName = "TabsLink";

// ============================================================================
// Exports
// ============================================================================
export { Tabs, TabsList, TabsTrigger, TabsContent, TabsLink, tabsListVariants, tabsTriggerVariants };
export type { TabsListProps, TabsTriggerProps, TabsLinkProps };
function _temp(child) {
  return React.isValidElement(child);
}
function _temp2(tab) {
  return tab.getAttribute("data-state") === "active";
}